import api from './axiosConfig';
import { handleApiError } from './errorHandler';

export const amazonAPI = {
    accounts: {
        getAll: async () => {
            try {
                const { data } = await api.get('/api/amazon/accounts/');
                return data;
            } catch (error) {
                handleApiError(error, 'Get all Amazon accounts error:');
            }
        },
        getById: async (id) => {
            try {
                const { data } = await api.get(`/api/amazon/accounts/${id}/`);
                return data;
            } catch (error) {
                handleApiError(error, 'Get Amazon account error:');
            }
        },
        create: async (accountData) => {
            try {
                const { data } = await api.post('/api/amazon/accounts/create/', accountData);
                return data;
            } catch (error) {
                handleApiError(error, 'Create Amazon account error:');
            }
        },
        update: async (id, accountData) => {
            try {
                const { data } = await api.put(`/api/amazon/accounts/${id}/update/`, accountData);
                return data;
            } catch (error) {
                handleApiError(error, 'Update Amazon account error:');
            }
        },
        delete: async (id) => {
            try {
                const { data } = await api.delete(`/api/amazon/accounts/${id}/delete/`);
                return data;
            } catch (error) {
                handleApiError(error, 'Delete Amazon account error:');
            }
        },
        testConnection: async (id, payload) => {
            try {
                const { data } = await api.post(`/api/amazon/accounts/${id}/test-connection/`, payload);
                return data;
            } catch (error) {
                handleApiError(error, 'Test Amazon connection error:');
            }
        },
        testGateway: async (id, payload) => {
            try {
                const { data } = await api.post(`/api/amazon/accounts/${id}/test-gateway/`, payload);
                return data;
            } catch (error) {
                handleApiError(error, 'Test gateway connection error:');
            }
        },
        testAdvertisingConnection: async (id, payload) => {
            try {
                const { data } = await api.post(`/api/amazon/accounts/${id}/test-advertising/`, payload);
                return data;
            } catch (error) {
                handleApiError(error, 'Test advertising connection error:');
            }
        },
        fetchAdvertisingProfiles: async (id, payload) => {
            try {
                const { data } = await api.post(`/api/amazon/accounts/${id}/advertising-profiles/`, payload);
                return data;
            } catch (error) {
                handleApiError(error, 'Fetch advertising profiles error:');
            }
        },
        getAdvertisingOAuthUrl: async (id) => {
            try {
                const { data } = await api.get(`/api/amazon/accounts/${id}/advertising-oauth-url/`);
                return data;
            } catch (error) {
                handleApiError(error, 'Get advertising OAuth URL error:');
            }
        },
        advertisingOAuthCallback: async (id, payload) => {
            try {
                const { data } = await api.post(`/api/amazon/accounts/${id}/advertising-oauth-callback/`, payload);
                return data;
            } catch (error) {
                handleApiError(error, 'Advertising OAuth callback error:');
            }
        }
    },
    queries: {
        getAll: async (params = {}) => {
            try {
                const query = new URLSearchParams(params).toString();
                const { data } = await api.get(`/api/amazon/queries/?${query}`);
                return data;
            } catch (error) {
                handleApiError(error, 'Get Data Kiosk queries error:');
            }
        },
        submit: async (accountPk, requestData) => {
            try {
                const { data } = await api.post(`/api/amazon/accounts/${accountPk}/queries/submit/`, requestData);
                return data;
            } catch (error) {
                handleApiError(error, 'Submit Data Kiosk query error:');
            }
        },
        retry: async (id) => {
            try {
                const { data } = await api.post(`/api/amazon/queries/${id}/retry/`);
                return data;
            } catch (error) {
                handleApiError(error, 'Retry Data Kiosk query error:');
            }
        },
        delete: async (id) => {
            try {
                const { data } = await api.delete(`/api/amazon/queries/${id}/delete/`);
                return data;
            } catch (error) {
                handleApiError(error, 'Delete Data Kiosk query error:');
            }
        },
        getById: async (id) => {
            try {
                const { data } = await api.get(`/api/amazon/queries/${id}/`);
                return data;
            } catch (error) {
                handleApiError(error, 'Get Data Kiosk query error:');
            }
        }
    },
    inventoryReports: {
        getAll: async (params = {}) => {
            try {
                const query = new URLSearchParams(params).toString();
                const { data } = await api.get(`/api/amazon/inventory-reports/?${query}`);
                return data;
            } catch (error) {
                handleApiError(error, 'Get inventory reports error:');
            }
        },
        request: async (accountPk) => {
            try {
                const { data } = await api.post(`/api/amazon/accounts/${accountPk}/inventory-reports/request/`);
                return data;
            } catch (error) {
                handleApiError(error, 'Request inventory report error:');
            }
        },
        delete: async (id) => {
            try {
                const { data } = await api.delete(`/api/amazon/inventory-reports/${id}/delete/`);
                return data;
            } catch (error) {
                handleApiError(error, 'Delete inventory report error:');
            }
        },
        getById: async (id) => {
            try {
                const { data } = await api.get(`/api/amazon/inventory-reports/${id}/`);
                return data;
            } catch (error) {
                handleApiError(error, 'Get inventory report error:');
            }
        }
    },
    inventorySnapshots: {
        getAll: async (params) => {
            try {
                const query = new URLSearchParams(params).toString();
                const { data } = await api.get(`/api/amazon/inventory-snapshots/?${query}`);
                return data;
            } catch (error) {
                handleApiError(error, 'Get inventory snapshots error:');
            }
        },
        getSummary: async (params) => {
            try {
                const query = new URLSearchParams(params).toString();
                const { data } = await api.get(`/api/amazon/inventory-snapshots/summary/?${query}`);
                return data;
            } catch (error) {
                handleApiError(error, 'Get inventory snapshot summary error:');
            }
        }
    },
    advertising: {
        getCampaigns: async (accountPk, stateFilter) => {
            try {
                const params = stateFilter ? `?state_filter=${stateFilter}` : '';
                const { data } = await api.get(`/api/amazon/accounts/${accountPk}/sp-campaigns/${params}`);
                return data;
            } catch (error) {
                handleApiError(error, 'Get SP campaigns error:');
            }
        },
        getAdGroups: async (accountPk, campaignIds) => {
            try {
                const params = campaignIds?.length ? `?campaign_ids=${campaignIds.join(',')}` : '';
                const { data } = await api.get(`/api/amazon/accounts/${accountPk}/sp-ad-groups/${params}`);
                return data;
            } catch (error) {
                handleApiError(error, 'Get SP ad groups error:');
            }
        },
        getKeywords: async (accountPk, adGroupIds) => {
            try {
                const params = adGroupIds?.length ? `?ad_group_ids=${adGroupIds.join(',')}` : '';
                const { data } = await api.get(`/api/amazon/accounts/${accountPk}/sp-keywords/${params}`);
                return data;
            } catch (error) {
                handleApiError(error, 'Get SP keywords error:');
            }
        },
        getTargets: async (accountPk, adGroupIds) => {
            try {
                const params = adGroupIds?.length ? `?ad_group_ids=${adGroupIds.join(',')}` : '';
                const { data } = await api.get(`/api/amazon/accounts/${accountPk}/sp-targets/${params}`);
                return data;
            } catch (error) {
                handleApiError(error, 'Get SP targets error:');
            }
        },
        getProductAds: async (accountPk, adGroupIds) => {
            try {
                const params = adGroupIds?.length ? `?ad_group_ids=${adGroupIds.join(',')}` : '';
                const { data } = await api.get(`/api/amazon/accounts/${accountPk}/sp-product-ads/${params}`);
                return data;
            } catch (error) {
                handleApiError(error, 'Get SP product ads error:');
            }
        },
        getAllSPData: async (accountPk, stateFilter) => {
            try {
                const params = stateFilter ? `?state_filter=${stateFilter}` : '';
                const { data } = await api.get(`/api/amazon/accounts/${accountPk}/sp-campaigns/all/${params}`);
                return data;
            } catch (error) {
                handleApiError(error, 'Get all SP data error:');
            }
        },
        triggerSpCacheSync: async (accountPk) => {
            try {
                const { data } = await api.post(`/api/amazon/accounts/${accountPk}/sp-cache/sync/`);
                return data;
            } catch (error) {
                handleApiError(error, 'Trigger SP cache sync error:');
            }
        },
        getSpCacheStatus: async (accountPk) => {
            try {
                const { data } = await api.get(`/api/amazon/accounts/${accountPk}/sp-cache/status/`);
                return data;
            } catch (error) {
                handleApiError(error, 'Get SP cache status error:');
            }
        },
        getSpCacheData: async (accountPk) => {
            try {
                const { data } = await api.get(`/api/amazon/accounts/${accountPk}/sp-cache/data/`);
                return data;
            } catch (error) {
                handleApiError(error, 'Get SP cache data error:');
            }
        },
    },
    bidding: {
        getEnrollments: async (accountId) => {
            try {
                const { data } = await api.get(`/api/amazon/bidding/enrollments/?account_id=${accountId}`);
                return data;
            } catch (error) {
                handleApiError(error, 'Get bidding enrollments error:');
            }
        },
        createEnrollment: async (enrollmentData) => {
            try {
                const { data } = await api.post('/api/amazon/bidding/enrollments/create/', enrollmentData);
                return data;
            } catch (error) {
                handleApiError(error, 'Create bidding enrollment error:');
            }
        },
        updateEnrollment: async (id, enrollmentData) => {
            try {
                const { data } = await api.put(`/api/amazon/bidding/enrollments/${id}/update/`, enrollmentData);
                return data;
            } catch (error) {
                handleApiError(error, 'Update bidding enrollment error:');
            }
        },
        deleteEnrollment: async (id) => {
            try {
                const { data } = await api.delete(`/api/amazon/bidding/enrollments/${id}/delete/`);
                return data;
            } catch (error) {
                handleApiError(error, 'Delete bidding enrollment error:');
            }
        },
        evaluate: async (accountId, enrollmentIds = null) => {
            try {
                const payload = { account_id: accountId };
                if (enrollmentIds) payload.enrollment_ids = enrollmentIds;
                const { data } = await api.post('/api/amazon/bidding/enrollments/evaluate/', payload);
                return data;
            } catch (error) {
                handleApiError(error, 'Evaluate bidding enrollments error:');
            }
        },
        getEvaluation: async (enrollmentPk) => {
            try {
                const { data } = await api.get(`/api/amazon/bidding/evaluations/${enrollmentPk}/`);
                return data;
            } catch (error) {
                handleApiError(error, 'Get bidding evaluation error:');
            }
        },
        getEvaluationHistory: async (enrollmentPk) => {
            try {
                const { data } = await api.get(`/api/amazon/bidding/evaluations/${enrollmentPk}/history/`);
                return data;
            } catch (error) {
                handleApiError(error, 'Get bidding evaluation history error:');
            }
        },
        getDashboardSummary: async (accountId) => {
            try {
                const { data } = await api.get(`/api/amazon/bidding/dashboard-summary/?account_id=${accountId}`);
                return data;
            } catch (error) {
                handleApiError(error, 'Get bidding dashboard summary error:');
            }
        },
    },
    economics: {
        getAll: async (params) => {
            try {
                const query = new URLSearchParams(params).toString();
                const { data } = await api.get(`/api/amazon/economics/?${query}`);
                return data;
            } catch (error) {
                handleApiError(error, 'Get Amazon economics error:');
            }
        },
        getSummary: async (params) => {
            try {
                const query = new URLSearchParams(params).toString();
                const { data } = await api.get(`/api/amazon/economics/summary/?${query}`);
                return data;
            } catch (error) {
                handleApiError(error, 'Get Amazon economics summary error:');
            }
        },
        export: async (params, format = 'csv') => {
            try {
                const response = await api.get('/api/amazon/economics/export/', {
                    params: { ...params, file_format: format },
                    responseType: 'blob',
                });

                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;

                const timestamp = new Date().toISOString()
                    .replace(/[:\-T]/g, '')
                    .slice(0, 14);
                const fileName = `amazon_economics_${timestamp}.${format === 'excel' ? 'xlsx' : 'csv'}`;
                link.setAttribute('download', fileName);

                document.body.appendChild(link);
                link.click();
                link.parentNode.removeChild(link);
                window.URL.revokeObjectURL(url);

                return true;
            } catch (error) {
                if (error.response?.data instanceof Blob) {
                    const text = await error.response.data.text();
                    try {
                        const json = JSON.parse(text);
                        throw new Error(json.error || json.detail || 'Export failed');
                    } catch {
                        throw new Error(text || 'Export failed');
                    }
                }
                handleApiError(error, 'Export Amazon economics error:');
                return false;
            }
        }
    },

    asinReports: {
        generate: async (accountPk, startDate, endDate, reportType = 'standard') => {
            try {
                const { data } = await api.post(`/api/amazon/accounts/${accountPk}/asin-reports/generate/`, {
                    start_date: startDate,
                    end_date: endDate,
                    report_type: reportType,
                });
                return data;
            } catch (error) {
                handleApiError(error, 'Generate ASIN report error:');
                return null;
            }
        },
        getAll: async (params = {}) => {
            try {
                const query = new URLSearchParams(params).toString();
                const { data } = await api.get(`/api/amazon/asin-reports/?${query}`);
                return data;
            } catch (error) {
                handleApiError(error, 'Get ASIN reports error:');
                return null;
            }
        },
        get: async (id, params = {}) => {
            try {
                const query = new URLSearchParams(params).toString();
                const { data } = await api.get(`/api/amazon/asin-reports/${id}/?${query}`);
                return data;
            } catch (error) {
                handleApiError(error, 'Get ASIN report error:');
                return null;
            }
        },
        getRows: async (id, params = '') => {
            try {
                const { data } = await api.get(`/api/amazon/asin-reports/${id}/rows/?${params}`);
                return data;
            } catch (error) {
                handleApiError(error, 'Get ASIN report rows error:');
                return null;
            }
        },
        delete: async (id) => {
            try {
                const { data } = await api.delete(`/api/amazon/asin-reports/${id}/delete/`);
                return data;
            } catch (error) {
                handleApiError(error, 'Delete ASIN report error:');
                return null;
            }
        },
        cancel: async (id) => {
            try {
                const { data } = await api.post(`/api/amazon/asin-reports/${id}/cancel/`);
                return data;
            } catch (error) {
                handleApiError(error, 'Cancel ASIN report error:');
                return null;
            }
        },
        retry: async (id) => {
            try {
                const { data } = await api.post(`/api/amazon/asin-reports/${id}/retry/`);
                return data;
            } catch (error) {
                handleApiError(error, 'Retry ASIN report error:');
                return null;
            }
        },
        getTacosTrend: async (params = {}) => {
            try {
                const query = new URLSearchParams(params).toString();
                const { data } = await api.get(`/api/amazon/asin-reports/tacos-trend/?${query}`);
                return data;
            } catch (error) {
                handleApiError(error, 'Get TACoS trend error:');
                return null;
            }
        },
        exportExcel: async (id, params = {}) => {
            try {
                const response = await api.get(`/api/amazon/asin-reports/${id}/export/`, {
                    params,
                    responseType: 'blob',
                });

                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;

                const timestamp = new Date().toISOString()
                    .replace(/[:\-T]/g, '')
                    .slice(0, 14);
                link.setAttribute('download', `asin_profitability_${timestamp}.xlsx`);

                document.body.appendChild(link);
                link.click();
                link.parentNode.removeChild(link);
                window.URL.revokeObjectURL(url);

                return true;
            } catch (error) {
                if (error.response?.data instanceof Blob) {
                    const text = await error.response.data.text();
                    try {
                        const json = JSON.parse(text);
                        throw new Error(json.error || json.detail || 'Export failed');
                    } catch (parseErr) {
                        if (parseErr.message !== 'Export failed' && parseErr instanceof SyntaxError) {
                            throw new Error(text || 'Export failed');
                        }
                        throw parseErr;
                    }
                }
                handleApiError(error, 'Export ASIN report error:');
                return false;
            }
        },
    },

    searchTermReports: {
        generate: async (accountPk, startDate, endDate) => {
            try {
                const { data } = await api.post(`/api/amazon/accounts/${accountPk}/search-term-reports/generate/`, {
                    start_date: startDate,
                    end_date: endDate,
                });
                return data;
            } catch (error) {
                handleApiError(error, 'Generate search term report error:');
                return null;
            }
        },
        getAll: async (params = {}) => {
            try {
                const query = new URLSearchParams(params).toString();
                const { data } = await api.get(`/api/amazon/search-term-reports/?${query}`);
                return data;
            } catch (error) {
                handleApiError(error, 'Get search term reports error:');
                return null;
            }
        },
        get: async (id) => {
            try {
                const { data } = await api.get(`/api/amazon/search-term-reports/${id}/`);
                return data;
            } catch (error) {
                handleApiError(error, 'Get search term report error:');
                return null;
            }
        },
        getRows: async (id, params = '') => {
            try {
                const { data } = await api.get(`/api/amazon/search-term-reports/${id}/rows/?${params}`);
                return data;
            } catch (error) {
                handleApiError(error, 'Get search term report rows error:');
                return null;
            }
        },
        delete: async (id) => {
            try {
                const { data } = await api.delete(`/api/amazon/search-term-reports/${id}/delete/`);
                return data;
            } catch (error) {
                handleApiError(error, 'Delete search term report error:');
                return null;
            }
        },
        cancel: async (id) => {
            try {
                const { data } = await api.post(`/api/amazon/search-term-reports/${id}/cancel/`);
                return data;
            } catch (error) {
                handleApiError(error, 'Cancel search term report error:');
                return null;
            }
        },
        retry: async (id) => {
            try {
                const { data } = await api.post(`/api/amazon/search-term-reports/${id}/retry/`);
                return data;
            } catch (error) {
                handleApiError(error, 'Retry search term report error:');
                return null;
            }
        },
    },

    adPerformance: {
        list: async (params = {}) => {
            try {
                const { data } = await api.get('/api/amazon/ad-performance/', { params });
                return data;
            } catch (error) {
                handleApiError(error, 'List ad performance reports error:');
                return null;
            }
        },
        get: async (id) => {
            try {
                const { data } = await api.get(`/api/amazon/ad-performance/${id}/`);
                return data;
            } catch (error) {
                handleApiError(error, 'Get ad performance report error:');
                return null;
            }
        },
        getRows: async (id, params = {}) => {
            try {
                const { data } = await api.get(`/api/amazon/ad-performance/${id}/rows/`, { params });
                return data;
            } catch (error) {
                handleApiError(error, 'Get ad performance rows error:');
                return null;
            }
        },
        delete: async (id) => {
            try {
                const { data } = await api.delete(`/api/amazon/ad-performance/${id}/delete/`);
                return data;
            } catch (error) {
                handleApiError(error, 'Delete ad performance report error:');
                return null;
            }
        },
    },

    traffic: {
        list: async (params = {}) => {
            try {
                const { data } = await api.get('/api/amazon/traffic/', { params });
                return data;
            } catch (error) {
                handleApiError(error, 'List traffic queries error:');
                return null;
            }
        },
        get: async (id) => {
            try {
                const { data } = await api.get(`/api/amazon/traffic/${id}/`);
                return data;
            } catch (error) {
                handleApiError(error, 'Get traffic query error:');
                return null;
            }
        },
        getRows: async (id, params = {}) => {
            try {
                const { data } = await api.get(`/api/amazon/traffic/${id}/rows/`, { params });
                return data;
            } catch (error) {
                handleApiError(error, 'Get traffic rows error:');
                return null;
            }
        },
        delete: async (id) => {
            try {
                const { data } = await api.delete(`/api/amazon/traffic/${id}/delete/`);
                return data;
            } catch (error) {
                handleApiError(error, 'Delete traffic query error:');
                return null;
            }
        },
    },

    inventoryPlanning: {
        list: async (params = {}) => {
            try {
                const { data } = await api.get('/api/amazon/inventory-planning/', { params });
                return data;
            } catch (error) {
                handleApiError(error, 'List inventory planning reports error:');
                return null;
            }
        },
        get: async (id) => {
            try {
                const { data } = await api.get(`/api/amazon/inventory-planning/${id}/`);
                return data;
            } catch (error) {
                handleApiError(error, 'Get inventory planning report error:');
                return null;
            }
        },
        getRows: async (id, params = {}) => {
            try {
                const { data } = await api.get(`/api/amazon/inventory-planning/${id}/rows/`, { params });
                return data;
            } catch (error) {
                handleApiError(error, 'Get inventory planning rows error:');
                return null;
            }
        },
        delete: async (id) => {
            try {
                const { data } = await api.delete(`/api/amazon/inventory-planning/${id}/delete/`);
                return data;
            } catch (error) {
                handleApiError(error, 'Delete inventory planning report error:');
                return null;
            }
        },
    },

    monthlyProfitability: {
        get: async (params) => {
            try {
                const { data } = await api.get('/api/amazon/monthly-profitability/', { params });
                return data;
            } catch (error) {
                handleApiError(error, 'Get monthly profitability error:');
                return null;
            }
        },
    },

    dailyDashboard: {
        get: async (params) => {
            try {
                const { data } = await api.get('/api/amazon/daily-dashboard/', { params });
                return data;
            } catch (error) {
                handleApiError(error, 'Get daily dashboard error:');
                return null;
            }
        },
    },

    collectionConfig: {
        get: async (accountPk) => {
            try {
                const { data } = await api.get(`/api/amazon/accounts/${accountPk}/collection-config/`);
                return data;
            } catch (error) {
                handleApiError(error, 'Get collection config error:');
                return null;
            }
        },

        update: async (accountPk, configData) => {
            try {
                const { data } = await api.put(`/api/amazon/accounts/${accountPk}/collection-config/update/`, configData);
                return data;
            } catch (error) {
                handleApiError(error, 'Update collection config error:');
                return null;
            }
        },
    },

    reportSettings: {
        get: async () => {
            try {
                const { data } = await api.get('/api/amazon/report-settings/');
                return data;
            } catch (error) {
                handleApiError(error, 'Get report settings error:');
                return null;
            }
        },

        update: async (settingsData) => {
            try {
                const { data } = await api.put('/api/amazon/report-settings/update/', settingsData);
                return data;
            } catch (error) {
                handleApiError(error, 'Update report settings error:');
                return null;
            }
        },
    },
};
